import { GenData, QEq } from '@educraft/base';
import { Addition } from './mathAddition';
import { Multiplication } from './mathMultiplication';
import { SimpleGenEq } from '../genHelper';

export const Verbal = {
  format: (template: string, ...args: any[]): string => {
    return template.replace(/{(\d+)}/g, (match, number) => {
      return typeof args[number] !== 'undefined' ? args[number] : match;
    });
  },

  plus:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const formats = [
        'součet čísel {0} a {1}',
        '{0} plus {1}',
        'o {0} více než {1}',
        '{0} zvětšené o {1}',
        'součet {0} a {1}',
        '{0} přičteno k {1}',
        '{0} a {1} dohromady',
        'suma {0} a {1}',
      ];
      const arr = numbersGen(data.random);
      const format = formats[data.random.next(formats.length)];
      return {
        text: Verbal.format(format, arr[0], arr[1]),
        result: arr[0] + arr[1],
      };
    },

  minus:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const formats = [
        'rozdíl čísel {0} a {1}',
        'rozdíl {0} a {1}',
        '{0} mínus {1}',
        'o {1} méně než {0}',
        '{0} zmenšené o {1}',
        '{1} odečteno od {0}',
        'úbytek {1} od {0}',
        '{0} snížené o {1}',
        '{0} bez {1}',
      ];
      const arr = numbersGen(data.random);
      const format = formats[data.random.next(formats.length)];
      const a = arr[0] + arr[1];
      const b = arr[1];
      return {
        text: Verbal.format(format, a, b),
        result: arr[0],
      };
    },

  multiply:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const baseFormats = ['součin čísel {0} a {1}', '{0} krát {1}', '{0} násobeno {1}'];
      const arr = numbersGen(data.random);
      const formats = [...baseFormats];
      const addSpecial = (val: number, other: number, index: number) => {
        const table: Record<number, string[]> = {
          2: ['dvojnásobek {1}', 'dva krát {1}'],
          3: ['trojnásobek {1}', 'tři krát {1}'],
          4: ['čtyřnásobek {1}', 'čtyři krát {1}'],
          5: ['pětinásobek {1}', 'pět krát {1}'],
          6: ['šestinásobek {1}', 'šest krát {1}'],
          7: ['sedminásobek {1}', 'sedm krát {1}'],
          8: ['osminásobek {1}', 'osm krát {1}'],
          9: ['devítinásobek {1}', 'devět krát {1}'],
          10: ['desetinásobek {1}', 'deset krát {1}'],
        };
        if (table[val]) {
          formats.push(...table[val].map((t) => (index === 0 ? t : t.replace('{1}', '{0}'))));
        }
      };
      addSpecial(arr[0], arr[1], 0);
      addSpecial(arr[1], arr[0], 1);

      const format = formats[data.random.next(formats.length)];
      return {
        text: Verbal.format(format, arr[0], arr[1]),
        result: arr[0] * arr[1],
      };
    },

  divide:
    (numbersGen: (r: any) => number[]) =>
    (data: GenData): QEq => {
      const baseFormats = ['podíl čísel {0} a {1}', '{0} děleno {1}'];
      const arr = numbersGen(data.random);
      const formats = [...baseFormats];
      const table: Record<number, string[]> = {
        2: ['polovina {0}', '{0} děleno dvěma', 'dvakrát méně než {0}'],
        3: ['třetina {0}', '{0} děleno třemi', 'třikrát méně než {0}'],
        4: ['čtvrtina {0}', '{0} děleno čtyřmi', 'čtyřikrát méně než {0}'],
        5: ['pětina {0}', '{0} děleno pěti', 'pětkrát méně než {0}'],
        6: ['šestina {0}', '{0} děleno šesti', 'šestkrát méně než {0}'],
        7: ['sedmina {0}', '{0} děleno sedmi', 'sedmkrát méně než {0}'],
        8: ['osmina {0}', '{0} děleno osmi', 'osmkrát méně než {0}'],
        9: ['devítina {0}', '{0} děleno devíti', 'devětkrát méně než {0}'],
        10: ['desítina {0}', '{0} děleno desíti', 'desetkrát méně než {0}'],
      };
      if (table[arr[1]]) formats.push(...table[arr[1]]);

      const format = formats[data.random.next(formats.length)];
      return {
        text: Verbal.format(format, arr[0] * arr[1], arr[1]),
        result: arr[0],
      };
    },
};

// Word Forms
export class Math_WordForm_Addition extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.simple()), 'Math_WordForm_Addition');
  }
}
export class Math_WordForm_Subtraction extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.simple()), 'Math_WordForm_Subtraction');
  }
}
export class Math_WordForm_Multiplication extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Addition.simple()), 'Math_WordForm_Multiplication');
  }
}
export class Math_WordForm_Division extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Addition.simple()), 'Math_WordForm_Division');
  }
}

// TimesTables Word Forms
export class Math_WordForm_Multiplication_TimesTables__All extends SimpleGenEq {
  constructor() {
    super(
      Verbal.multiply(Multiplication.simple([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])),
      'Math_WordForm_Multiplication_TimesTables__All'
    );
  }
}
export class Math_WordForm_Multiplication_TimesTables__Harder extends SimpleGenEq {
  constructor() {
    super(
      Verbal.multiply(Multiplication.advanced()),
      'Math_WordForm_Multiplication_TimesTables__Harder'
    );
  }
}
export class Math_WordForm_Multiplication_TimesTables__Mini extends SimpleGenEq {
  constructor() {
    super(
      Verbal.multiply(Multiplication.simple([2, 3, 4], 1, 5)),
      'Math_WordForm_Multiplication_TimesTables__Mini'
    );
  }
}

export class Math_WordForm_Division_TimesTables__All extends SimpleGenEq {
  constructor() {
    super(
      Verbal.divide(Multiplication.simple([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 1)),
      'Math_WordForm_Division_TimesTables__All'
    );
  }
}
export class Math_WordForm_Division_TimesTables__Harder extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.advanced()), 'Math_WordForm_Division_TimesTables__Harder');
  }
}

// Addition Word Forms
export class Math_WordForm_Addition__To5__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.single(2, 1, 5)), 'Math_WordForm_Addition__To5__Base');
  }
}
export class Math_WordForm_Addition__To10__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.single(2, 1, 10)), 'Math_WordForm_Addition__To10__Base');
  }
}
export class Math_WordForm_Addition__To20__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.single(2, 1, 20)), 'Math_WordForm_Addition__To20__Base');
  }
}
export class Math_WordForm_Addition__To20__Carry extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.carrySingleDigit(20)), 'Math_WordForm_Addition__To20__Carry');
  }
}
export class Math_WordForm_Addition__To100__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.single(2, 1, 100)), 'Math_WordForm_Addition__To100__Base');
  }
}
export class Math_WordForm_Addition__To100__Carry extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.carrySingleDigit(100)), 'Math_WordForm_Addition__To100__Carry');
  }
}
export class Math_WordForm_Addition__To100__NoCarry extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.noCarry(2, 2)), 'Math_WordForm_Addition__To100__NoCarry');
  }
}
export class Math_WordForm_Addition__To1000__NoCarry extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.noCarry(2, 3)), 'Math_WordForm_Addition__To1000__NoCarry');
  }
}
export class Math_WordForm_Addition__To1000000__NoCarry extends SimpleGenEq {
  constructor() {
    super(Verbal.plus(Addition.noCarry(2, 6)), 'Math_WordForm_Addition__To1000000__NoCarry');
  }
}

// Subtraction Word Forms
export class Math_WordForm_Subtraction__To5__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.single(2, 1, 5)), 'Math_WordForm_Subtraction__To5__Base');
  }
}
export class Math_WordForm_Subtraction__To10__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.single(2, 1, 10)), 'Math_WordForm_Subtraction__To10__Base');
  }
}
export class Math_WordForm_Subtraction__To20__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.single(2, 1, 20)), 'Math_WordForm_Subtraction__To20__Base');
  }
}
export class Math_WordForm_Subtraction__To20__Carry extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.carrySingleDigit(20)), 'Math_WordForm_Subtraction__To20__Carry');
  }
}
export class Math_WordForm_Subtraction__To100__Base extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.single(2, 1, 100)), 'Math_WordForm_Subtraction__To100__Base');
  }
}
export class Math_WordForm_Subtraction__To100__Carry extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.carrySingleDigit(100)), 'Math_WordForm_Subtraction__To100__Carry');
  }
}
export class Math_WordForm_Subtraction__To100__NoCarry extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.noCarry(2, 2)), 'Math_WordForm_Subtraction__To100__NoCarry');
  }
}
export class Math_WordForm_Subtraction__To1000__NoCarry extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.noCarry(2, 3)), 'Math_WordForm_Subtraction__To1000__NoCarry');
  }
}
export class Math_WordForm_Subtraction__To1000000__NoCarry extends SimpleGenEq {
  constructor() {
    super(Verbal.minus(Addition.noCarry(2, 6)), 'Math_WordForm_Subtraction__To1000000__NoCarry');
  }
}

// Multiplication Word Forms
export class Math_WordForm_Multiplication__1 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([1])), 'Math_WordForm_Multiplication__1');
  }
}
export class Math_WordForm_Multiplication__2 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([2])), 'Math_WordForm_Multiplication__2');
  }
}
export class Math_WordForm_Multiplication__3 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([3])), 'Math_WordForm_Multiplication__3');
  }
}
export class Math_WordForm_Multiplication__4 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([4])), 'Math_WordForm_Multiplication__4');
  }
}
export class Math_WordForm_Multiplication__5 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([5])), 'Math_WordForm_Multiplication__5');
  }
}
export class Math_WordForm_Multiplication__6 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([6])), 'Math_WordForm_Multiplication__6');
  }
}
export class Math_WordForm_Multiplication__7 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([7])), 'Math_WordForm_Multiplication__7');
  }
}
export class Math_WordForm_Multiplication__8 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([8])), 'Math_WordForm_Multiplication__8');
  }
}
export class Math_WordForm_Multiplication__9 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([9])), 'Math_WordForm_Multiplication__9');
  }
}
export class Math_WordForm_Multiplication__10 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([10])), 'Math_WordForm_Multiplication__10');
  }
}
export class Math_WordForm_Multiplication__11 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([11])), 'Math_WordForm_Multiplication__11');
  }
}
export class Math_WordForm_Multiplication__12 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([12])), 'Math_WordForm_Multiplication__12');
  }
}
export class Math_WordForm_Multiplication__13 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([13])), 'Math_WordForm_Multiplication__13');
  }
}
export class Math_WordForm_Multiplication__14 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([14])), 'Math_WordForm_Multiplication__14');
  }
}
export class Math_WordForm_Multiplication__15 extends SimpleGenEq {
  constructor() {
    super(Verbal.multiply(Multiplication.simple([15])), 'Math_WordForm_Multiplication__15');
  }
}

// Division Word Forms
export class Math_WordForm_Division__2 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([2], 1)), 'Math_WordForm_Division__2');
  }
}
export class Math_WordForm_Division__3 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([3], 1)), 'Math_WordForm_Division__3');
  }
}
export class Math_WordForm_Division__4 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([4], 1)), 'Math_WordForm_Division__4');
  }
}
export class Math_WordForm_Division__5 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([5], 1)), 'Math_WordForm_Division__5');
  }
}
export class Math_WordForm_Division__6 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([6], 1)), 'Math_WordForm_Division__6');
  }
}
export class Math_WordForm_Division__7 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([7], 1)), 'Math_WordForm_Division__7');
  }
}
export class Math_WordForm_Division__8 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([8], 1)), 'Math_WordForm_Division__8');
  }
}
export class Math_WordForm_Division__9 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([9], 1)), 'Math_WordForm_Division__9');
  }
}
export class Math_WordForm_Division__10 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([10], 1)), 'Math_WordForm_Division__10');
  }
}
export class Math_WordForm_Division__11 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([11], 1)), 'Math_WordForm_Division__11');
  }
}
export class Math_WordForm_Division__12 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([12], 1)), 'Math_WordForm_Division__12');
  }
}
export class Math_WordForm_Division__13 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([13], 1)), 'Math_WordForm_Division__13');
  }
}
export class Math_WordForm_Division__14 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([14], 1)), 'Math_WordForm_Division__14');
  }
}
export class Math_WordForm_Division__15 extends SimpleGenEq {
  constructor() {
    super(Verbal.divide(Multiplication.simple([15], 1)), 'Math_WordForm_Division__15');
  }
}
